/**
 * <mj-reviews> -- the next arrow on the mobile review scroller.
 *
 * The row is a native scroller, so it already swipes, wheel-scrolls and keyboard-scrolls
 * with no script at all. This file only adds the arrow the comp draws at the right edge,
 * which is why the button ships `hidden` and is revealed here: an arrow that cannot work
 * is worse than no arrow.
 *
 * It scrolls by one card and wraps to the start at the end, rather than disabling there.
 * There is only a forward arrow in the comp, so stopping dead would leave no way back.
 */

class MjReviews extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-mj-rev-track]');
    this.next = this.querySelector('[data-mj-rev-next]');
    if (!this.track || !this.next) return;

    this.next.addEventListener('click', () => this.#advance());

    // The arrow is only useful while there is something past the edge, and only drawn
    // below 768. Both are answered by measuring, so one check covers them.
    this.sync = () => this.#sync();
    this.sync();

    this.observer = new ResizeObserver(this.sync);
    this.observer.observe(this.track);
    this.track.addEventListener('scroll', this.sync, { passive: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.track?.removeEventListener('scroll', this.sync);
  }

  #sync() {
    // A 1px slack: fractional card widths mean scrollWidth and clientWidth rarely agree
    // exactly even when the row has nothing left to show.
    this.next.hidden = this.track.scrollWidth - this.track.clientWidth <= 1;
  }

  #advance() {
    const item = this.track.firstElementChild;
    if (!item) return;

    const step = item.getBoundingClientRect().width + this.#gap();
    const end = this.track.scrollWidth - this.track.clientWidth;

    // Within one step of the end counts as the end: scrolling a fraction of a card and
    // calling it a turn reads as a glitch rather than a move.
    const atEnd = this.track.scrollLeft >= end - step / 2;
    this.track.scrollTo({ left: atEnd ? 0 : this.track.scrollLeft + step });
  }

  #gap() {
    const gap = parseFloat(getComputedStyle(this.track).columnGap);
    return Number.isNaN(gap) ? 0 : gap;
  }
}

if (!customElements.get('mj-reviews')) {
  customElements.define('mj-reviews', MjReviews);
}
